import { addDays } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ErrorStateFallback } from '@/components/ui/error-state';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { INDEED } from '@/types/channels';

import { getChannelDefaults } from './channel-defaults';
import { Header } from './header';
import { JobPostingFlow } from './job-posting-flow';

export function JobPosting() {
  const location = useLocation();
  const {
    handlePostJob,
    clearJobPostingStorage,
    selectedChannelSlugs,
    selectedOpeningIds,
    unsupportedOpenings,
    hookForm,
  } = useJobPostingContext();
  const { setValue, getValues } = hookForm;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handlePostJob();
  };

  // Clear storage when user navigates away from /post-job route. This won't trigger on page refresh since the component remounts with the same location
  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/post-job')) {
        clearJobPostingStorage();
      }
    };
  }, [location, clearJobPostingStorage]);

  // Opening budget for Zip will be 149
  const getOpeningBudget = useCallback((channelSlug: string) => {
    const fixedOpeningBudget = getChannelDefaults(channelSlug).budget;
    if (!fixedOpeningBudget) return null;
    return fixedOpeningBudget;
  }, []);

  const generateOpeningBudgets = useCallback(
    (channelSlug: string) => {
      return selectedOpeningIds.reduce((acc, openingId) => {
        if (unsupportedOpenings[channelSlug]?.includes(openingId)) {
          return acc;
        }
        return {
          ...acc,
          [openingId]: getOpeningBudget(channelSlug),
        };
      }, {});
    },
    [getOpeningBudget, selectedOpeningIds, unsupportedOpenings],
  );

  //  Channel budget for Zip will be 149 * number of openings
  const getChannelBudget = useCallback(
    (channelSlug: string) => {
      const fixedOpeningBudget = getChannelDefaults(channelSlug).budget;
      if (!fixedOpeningBudget) return null;
      return (
        fixedOpeningBudget *
        Object.keys(generateOpeningBudgets(channelSlug)).length
      );
    },
    [generateOpeningBudgets],
  );

  const getChannelEndDate = useCallback((channelSlug: string) => {
    const fixedDuration = getChannelDefaults(channelSlug).duration;
    if (!fixedDuration) return null;
    return addDays(new Date(), fixedDuration).toISOString();
  }, []);

  // Initialize the default form data for channels based on the selected channels and openings.
  useEffect(() => {
    const existingChannels = getValues('channels');
    // Only initialize if we have selected channels and haven't initialized yet
    if (selectedChannelSlugs.length > 0 && !existingChannels) {
      setValue(
        'channels',
        selectedChannelSlugs.reduce(
          (acc, channelSlug) => ({
            ...acc,
            [channelSlug]: {
              startsAt: new Date().toISOString(),
              budgetAllocation: 'evenly' as const,
              budgetType: 'lifetime' as const,
              openingBudgets: generateOpeningBudgets(channelSlug),
              endAt: getChannelEndDate(channelSlug),
              channelBudget: getChannelBudget(channelSlug),
              totalBudget: null,
              isExpanded: true,
              endAtType: channelSlug === INDEED ? 'fixed' : null, // Indeed is the only channel with end date type
              slug: channelSlug,
              isValidated: false,
            },
          }),
          {},
        ),
      );
    }
  }, [
    selectedChannelSlugs,
    setValue,
    getValues,
    generateOpeningBudgets,
    getChannelBudget,
    getChannelEndDate,
  ]);

  return (
    <ErrorStateFallback>
      <form onSubmit={handleSubmit}>
        <div className="flex h-screen flex-col gap-6">
          <Header />

          <JobPostingFlow />
        </div>
      </form>
    </ErrorStateFallback>
  );
}
